/**
 * Deterministic, per-account positions dataset.
 *
 * Design goals:
 *  • Every position has the full Alpaca-style shape (avg entry, cost
 *    basis, qty, side, today's P/L, total P/L, price/value) so the
 *    Advanced table can render without per-row recomputation.
 *  • At least two shorts exist across the dataset so the Long/Short
 *    filter + Side column have something to show.
 *  • Each account's `stocksCount` in mock-data.ts should roughly match
 *    the number of positions we generate (±1 is fine — positions are
 *    illustrative, not contractual).
 *  • Seeded generation — output is stable across reloads so
 *    screenshots don't drift.
 */

// ---- Types ---------------------------------------------------------------

/** Position direction. "short" positions have negated totalPL math. */
export type PositionSide = "long" | "short";

/** Coarse asset class, mirrors Alpaca's Columns > Asset Class filter. */
export type AssetClass = "stocks" | "etf" | "crypto" | "options";

export interface Position {
  id: string;
  accountId: string;
  /** Bot that opened/owns this position. `undefined` = manual / outside-of-bot trade. */
  botId?: string;
  /** Ticker symbol, e.g. "AAPL". */
  asset: string;
  /** Full instrument name, e.g. "Apple Inc.". Used for the Simple view. */
  assetName: string;
  assetClass: AssetClass;
  side: PositionSide;
  /** Absolute (always positive) quantity. Side tells you sign. */
  qty: number;
  avgEntryPrice: number;
  currentPrice: number;
  /** qty × currentPrice (positive regardless of side — this is the $ tied up). */
  marketValue: number;
  /** qty × avgEntryPrice. */
  costBasis: number;
  /** Today's underlying % move, signed. */
  dayChangePct: number;
  /** Today's $ P/L for this position (negated for shorts when day is up). */
  todayPLDollar: number;
  /** Today's % P/L — same as `dayChangePct` for longs; negated for shorts. */
  todayPLPct: number;
  /** Since-open $ P/L. */
  totalPLDollar: number;
  /** Since-open % P/L relative to cost basis. */
  totalPLPct: number;
  /** Short price series for the Simple-view mini chart. */
  sparkline: number[];
  /** ISO yyyy-mm-dd when the position was first opened. */
  openedAt: string;
  /**
   * Set by `mergePositionsByTicker` when an All-Live aggregate row
   * sums multiple legs of the same ticker across bots. Drawer reads
   * these to render "Multiple bots" / "Multiple accounts" instead of
   * a single name. Both undefined for un-merged rows (the common
   * case, including every per-account view). Sergey 2026-04-27.
   */
  botCount?: number;
  accountCount?: number;
}

// ---- Asset catalog -------------------------------------------------------

interface AssetSpec {
  ticker: string;
  name: string;
  cls: AssetClass;
  /** Current price used to build market value. */
  price: number;
  /** Today's underlying price move as a decimal (±). */
  dayPct: number;
}

/**
 * Tickers pulled from the Orders + Bots data + a handful of extras so
 * the dataset looks like a plausible multi-strategy brokerage account.
 * Prices are snapshot-style (not live).
 */
const ASSETS: AssetSpec[] = [
  // Mega-cap tech (matches mean-reversion + momentum bots)
  { ticker: "AAPL",  name: "Apple Inc.",                             cls: "stocks", price: 214.80, dayPct:  0.0082 },
  { ticker: "MSFT",  name: "Microsoft Corp.",                        cls: "stocks", price: 428.12, dayPct:  0.0051 },
  { ticker: "NVDA",  name: "NVIDIA Corp.",                           cls: "stocks", price: 138.55, dayPct:  0.0204 },
  { ticker: "GOOGL", name: "Alphabet Inc. Class A",                  cls: "stocks", price: 185.42, dayPct:  0.0073 },
  { ticker: "META",  name: "Meta Platforms Inc.",                    cls: "stocks", price: 578.24, dayPct: -0.0044 },
  { ticker: "AMZN",  name: "Amazon.com Inc.",                        cls: "stocks", price: 221.88, dayPct:  0.0038 },
  { ticker: "TSLA",  name: "Tesla Inc.",                             cls: "stocks", price: 282.45, dayPct: -0.0181 },
  { ticker: "AMD",   name: "Advanced Micro Devices Inc.",            cls: "stocks", price: 162.10, dayPct:  0.0129 },
  { ticker: "AVGO",  name: "Broadcom Inc.",                          cls: "stocks", price: 178.22, dayPct:  0.0062 },
  { ticker: "TSM",   name: "Taiwan Semiconductor Manufacturing ADR", cls: "stocks", price: 195.68, dayPct:  0.0108 },

  // Index ETFs (conservative rotation + risk parity)
  { ticker: "SPY",   name: "SPDR S&P 500 ETF Trust",                 cls: "etf",    price: 582.44, dayPct:  0.0041 },
  { ticker: "QQQ",   name: "Invesco QQQ Trust",                      cls: "etf",    price: 512.33, dayPct:  0.0068 },
  { ticker: "IWM",   name: "iShares Russell 2000 ETF",               cls: "etf",    price: 224.11, dayPct: -0.0092 },
  { ticker: "VDE",   name: "Vanguard Energy Index Fund ETF",         cls: "etf",    price: 138.02, dayPct:  0.0008 },
  { ticker: "LVHI",  name: "Franklin International Low Volatility High Dividend", cls: "etf", price: 26.88, dayPct: 0.0141 },

  // Bonds & cash-like
  { ticker: "IEF",   name: "iShares 7-10 Year Treasury Bond ETF",    cls: "etf",    price:  95.14, dayPct: -0.0014 },
  { ticker: "BIL",   name: "SPDR Bloomberg 1-3 Month T-Bill ETF",    cls: "etf",    price:  91.48, dayPct:  0.0008 },
  { ticker: "BOND",  name: "PIMCO Active Bond Exchange-Traded Fund", cls: "etf",    price:  94.10, dayPct:  0.0056 },

  // Financials / defensives
  { ticker: "JPM",   name: "JPMorgan Chase & Co.",                   cls: "stocks", price: 224.70, dayPct:  0.0028 },
  { ticker: "BAC",   name: "Bank of America Corp.",                  cls: "stocks", price:  45.22, dayPct: -0.0055 },
  { ticker: "JNJ",   name: "Johnson & Johnson",                      cls: "stocks", price: 158.10, dayPct:  0.0012 },
  { ticker: "XOM",   name: "Exxon Mobil Corp.",                      cls: "stocks", price: 119.42, dayPct: -0.0103 },

  // Consumer / other
  { ticker: "COST",  name: "Costco Wholesale Corp.",                 cls: "stocks", price: 932.55, dayPct:  0.0204 },
  { ticker: "CASY",  name: "Casey's General Stores Inc.",            cls: "stocks", price: 392.18, dayPct:  0.0295 },
  { ticker: "CACI",  name: "CACI International Inc. Class A",        cls: "stocks", price: 490.22, dayPct: -0.0081 },

  // Small-caps / speculative
  { ticker: "PLTR",  name: "Palantir Technologies Inc.",             cls: "stocks", price:  69.14, dayPct:  0.0321 },
  { ticker: "COIN",  name: "Coinbase Global Inc.",                   cls: "stocks", price: 285.40, dayPct: -0.0248 },

  // Crypto (for the Asset Class filter to do something)
  { ticker: "BTCUSD", name: "Bitcoin (USD)",                         cls: "crypto", price: 72420.00, dayPct:  0.0116 },
  { ticker: "ETHUSD", name: "Ethereum (USD)",                        cls: "crypto", price:  3810.25, dayPct: -0.0072 }
];

// ---- Deterministic helpers ----------------------------------------------

/**
 * Mulberry32 — small, seedable PRNG. Output sequences are stable across
 * runs so screenshots don't drift.
 */
function mulberry32(seed: number) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash a string into a uint32 for use as a seed. */
function hashStr(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** 60-point random walk that ends near `end`, used for sparklines. */
function walk(end: number, steps: number, vol: number, drift: number, seed: number): number[] {
  const rnd = mulberry32(seed);
  const out: number[] = [];
  // Start far enough back that drift + noise walks roughly to `end`.
  let price = end / (1 + drift * steps);
  for (let i = 0; i < steps; i++) {
    const shock = (rnd() - 0.5) * 2 * vol;
    price = price * (1 + drift + shock);
    out.push(price);
  }
  // Force endpoint so the spark visually matches currentPrice.
  out[out.length - 1] = end;
  return out;
}

// ---- Per-account position generation -------------------------------------

/** How many positions each account should carry, keyed by id. */
const ACCOUNT_POSITION_COUNTS: Record<string, number> = {
  "acc-live-1": 9,   // Conservative rotation — SPY/QQQ/IEF heavy
  "acc-live-2": 12,  // Swing tech — mega-caps + 1 short
  "acc-1":      14,  // WhiteHatBots — many small positions
  "acc-2":      18,  // Alpaca Paper — large portfolio
  "acc-3":      11   // Ellen — mid-sized
};

/**
 * Which tickers each account leans on. Entries are weighted — tickers
 * listed earlier are more likely to be picked. Having per-account
 * thematic tickers makes the demo read as "this bot is conservative,
 * that one is tech-swing" rather than a random spray.
 */
const ACCOUNT_TICKER_PREFERENCES: Record<string, string[]> = {
  "acc-live-1": ["SPY","QQQ","IEF","BIL","BOND","LVHI","VDE","JNJ","JPM","XOM","AAPL","MSFT"],
  "acc-live-2": ["AAPL","MSFT","NVDA","GOOGL","META","AMZN","AMD","TSM","AVGO","TSLA","COIN","PLTR","COST","CASY"],
  "acc-1":      ["AAPL","MSFT","NVDA","GOOGL","META","TSLA","AMD","AVGO","JPM","BAC","COST","PLTR","COIN","TSM","AMZN","CACI","CASY","BTCUSD","ETHUSD"],
  "acc-2":      ["SPY","QQQ","IWM","AAPL","MSFT","NVDA","GOOGL","META","AMZN","JPM","JNJ","COST","CASY","CACI","AVGO","TSM","XOM","VDE","BAC","BOND","IEF"],
  "acc-3":      ["SPY","QQQ","AAPL","MSFT","NVDA","GOOGL","META","AMZN","COST","JPM","JNJ","VDE","AMD","BAC"]
};

/**
 * Positions seeded with `short: true` — explicit overrides so we can
 * control exactly where shorts show up regardless of RNG.
 */
const FORCED_SHORTS: Array<{ accountId: string; ticker: string }> = [
  { accountId: "acc-live-2", ticker: "TSLA" },  // mean-reversion short on TSLA
  { accountId: "acc-2",      ticker: "COIN" },  // speculative short
  { accountId: "acc-1",      ticker: "AMD"  }   // one short in paper account
];

function isShort(accountId: string, ticker: string): boolean {
  return FORCED_SHORTS.some((s) => s.accountId === accountId && s.ticker === ticker);
}

/**
 * Build N positions for one account. `targetEquity` is the sum of
 * `stocksValue` from BrokerAccount — we scale qty per-position so the
 * portfolio total lands in the right neighbourhood.
 */
function buildPositionsForAccount(
  accountId: string,
  botIds: string[],
  targetEquity: number
): Position[] {
  const count = ACCOUNT_POSITION_COUNTS[accountId] ?? 10;
  const preferred = ACCOUNT_TICKER_PREFERENCES[accountId] ?? ASSETS.map((a) => a.ticker);
  const picked = preferred.slice(0, count);

  // Force shorts into the pick list if they aren't already there.
  for (const s of FORCED_SHORTS) {
    if (s.accountId === accountId && !picked.includes(s.ticker)) {
      picked.push(s.ticker);
    }
  }

  // Weights: earlier = larger allocation. Normalised at the end so the
  // sum of marketValues roughly matches targetEquity.
  const weights = picked.map((_, i) => 1 / (i * 0.25 + 1));
  const wSum = weights.reduce((a, b) => a + b, 0);

  const positions: Position[] = [];
  for (let i = 0; i < picked.length; i++) {
    const ticker = picked[i]!;
    const asset = ASSETS.find((a) => a.ticker === ticker);
    if (!asset) continue;

    const seed = hashStr(`${accountId}|${ticker}`);
    const rnd = mulberry32(seed);

    const side: PositionSide = isShort(accountId, ticker) ? "short" : "long";
    const weight = weights[i]! / wSum;
    const targetValue = targetEquity * weight;
    const qtyRaw = targetValue / asset.price;
    // Round sensibly — integers for cheap stocks, fractional for high-$.
    const qty = asset.price > 200
      ? Math.max(0.01, Math.round(qtyRaw * 100) / 100)
      : asset.price > 50
        ? Math.max(0.1, Math.round(qtyRaw * 10) / 10)
        : Math.max(1, Math.round(qtyRaw));

    // Avg entry drifts ±15% around current — large enough to make
    // Total P/L interesting, small enough to stay believable.
    const drift = (rnd() - 0.5) * 0.30;
    const avgEntryPrice = asset.price / (1 + drift);

    const marketValue = qty * asset.price;
    const costBasis = qty * avgEntryPrice;

    // For longs: price up → profit. For shorts: price up → loss.
    const pricePL = asset.price - avgEntryPrice;
    const totalPLDollar = side === "long" ? pricePL * qty : -pricePL * qty;
    const totalPLPct = costBasis > 0 ? totalPLDollar / costBasis : 0;

    const todayPLPct = side === "long" ? asset.dayPct : -asset.dayPct;
    // Today's $ move = qty * (price * dayPct) since dayPct is the
    // underlying's move, not the position's return.
    const todayPLDollar = qty * asset.price * asset.dayPct * (side === "long" ? 1 : -1);

    // Sparkline ends at current price. Vol tuned per asset class.
    const vol = asset.cls === "crypto" ? 0.025
              : asset.cls === "etf" && (ticker === "BIL" || ticker === "IEF" || ticker === "BOND") ? 0.003
              : 0.015;
    const driftPerStep = totalPLPct / 60;
    const sparkline = walk(asset.price, 60, vol, driftPerStep, seed);

    // Pick a bot to own it. For accounts with 1 bot, it's that bot.
    // For multi-bot accounts (rare in the current dataset) we round-robin.
    // A small tail (~15% of positions) is "manual" — no botId — to
    // exercise the detail-panel empty state.
    const manualRoll = rnd();
    const botId = manualRoll < 0.15 ? undefined : botIds[i % Math.max(1, botIds.length)];

    // Opened-at: within the last 30–240 days.
    const daysAgo = 30 + Math.floor(rnd() * 210);
    const openedAt = new Date(Date.now() - daysAgo * 86400_000)
      .toISOString()
      .slice(0, 10);

    positions.push({
      id: `pos-${accountId}-${ticker}`,
      accountId,
      botId,
      asset: ticker,
      assetName: asset.name,
      assetClass: asset.cls,
      side,
      qty,
      avgEntryPrice,
      currentPrice: asset.price,
      marketValue,
      costBasis,
      dayChangePct: asset.dayPct,
      todayPLDollar,
      todayPLPct,
      totalPLDollar,
      totalPLPct,
      sparkline,
      openedAt
    });
  }

  return positions;
}

// ---- Account stub list --------------------------------------------------
// Kept in this file (rather than importing ACCOUNTS) to avoid a cycle —
// mock-positions loads before the large ACCOUNTS array is fully built.
// Numbers here just need to roughly agree with BrokerAccount.stocksValue.

const POSITION_SEED: Array<{ id: string; botIds: string[]; stocksValue: number }> = [
  { id: "acc-live-1", botIds: ["bot-live-momentum"], stocksValue: 28772.32 },
  { id: "acc-live-2", botIds: ["bot-live-swing"],    stocksValue: 87210.75 },
  { id: "acc-1",      botIds: ["bot-rsi2-v2"],       stocksValue: 8859.89 },
  { id: "acc-2",      botIds: ["bot-paper-v2"],      stocksValue: 169017.65 },
  { id: "acc-3",      botIds: ["bot-ellen-rsi"],     stocksValue: 125393.04 }
];

export const POSITIONS: Position[] = POSITION_SEED.flatMap((seed) =>
  buildPositionsForAccount(seed.id, seed.botIds, seed.stocksValue)
);

// ---- Account-level derived summaries -------------------------------------

export interface PositionsPortfolioSummary {
  count: number;
  longCount: number;
  shortCount: number;
  totalMarketValue: number;
  totalCostBasis: number;
  totalTodayPL: number;
  weightedTodayPLPct: number;
  totalUnrealizedPL: number;
  totalUnrealizedPLPct: number;
}

/**
 * Blend positions into the Portfolio summary row shown at the top of
 * the Advanced table. Dollar totals are pure sums; percentages are
 * market-value-weighted (today's % is weighted by market value,
 * total % uses totalPL$ / costBasis$ so it reads as "cost-weighted").
 */
export function summarizePositions(positions: Position[]): PositionsPortfolioSummary {
  let totalMV = 0;
  let totalCB = 0;
  let totalTodayPL = 0;
  let totalTotalPL = 0;
  let longCount = 0;
  let shortCount = 0;
  for (const p of positions) {
    totalMV += p.marketValue;
    totalCB += p.costBasis;
    totalTodayPL += p.todayPLDollar;
    totalTotalPL += p.totalPLDollar;
    if (p.side === "long") longCount++;
    else shortCount++;
  }
  return {
    count: positions.length,
    longCount,
    shortCount,
    totalMarketValue: totalMV,
    totalCostBasis: totalCB,
    totalTodayPL,
    weightedTodayPLPct: totalMV > 0 ? totalTodayPL / totalMV : 0,
    totalUnrealizedPL: totalTotalPL,
    totalUnrealizedPLPct: totalCB > 0 ? totalTotalPL / totalCB : 0
  };
}

/** Return the subset of positions that belong to the given accounts. */
export function positionsForAccounts(accountIds: string[]): Position[] {
  const set = new Set(accountIds);
  return POSITIONS.filter((p) => set.has(p.accountId));
}

/**
 * Roll up positions that share a ticker into a single aggregated row.
 *
 * Used by the All-Live "Positions" tab: when V (VISA) is held in two
 * accounts and across three bots, we want one row labelled `V` with
 * the summed market value, summed cost basis, weighted avg entry, and
 * the drawer surfacing "Multiple accounts" / "Multiple bots" rather
 * than picking one. Per-account / single-account views never call
 * this — duplicates only appear at the aggregate level.
 *
 * Aggregation rules (all signed by side; engine assumes side is
 * consistent across legs of the same ticker, which Alpaca enforces
 * for cash accounts):
 *   • qty, marketValue, costBasis, todayPLDollar, totalPLDollar — sum
 *   • avgEntryPrice — weighted = Σcostbasis / Σqty
 *   • currentPrice, dayChangePct, sparkline — taken from the first leg
 *     (they're per-ticker; identical across legs by construction)
 *   • todayPLPct — Σtoday$ / Σmarketvalue (weighted by exposure today)
 *   • totalPLPct — Σtotal$ / Σcostbasis
 *   • side, assetClass, asset, assetName — taken from the first leg
 *     (identical across legs)
 *   • openedAt — earliest (the position has been "open" since then)
 *   • id, accountId — synthesised; botId cleared
 *   • botCount / accountCount — set so the drawer renders "Multiple
 *     bots/accounts" when > 1
 *
 * Returns positions in insertion order of the first-seen ticker so the
 * row order remains stable across renders.
 */
export function mergePositionsByTicker(positions: Position[]): Position[] {
  const groups = new Map<string, Position[]>();
  const order: string[] = [];
  for (const p of positions) {
    const key = p.asset.toUpperCase();
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(p);
  }
  const out: Position[] = [];
  for (const ticker of order) {
    const legs = groups.get(ticker)!;
    if (legs.length === 1) {
      // Single leg — even in aggregate view, ticker held in one
      // account/bot; pass through unchanged so the drawer shows the
      // real bot/account name.
      out.push(legs[0]!);
      continue;
    }
    const head = legs[0]!;
    const totalQty = legs.reduce((s, p) => s + p.qty, 0);
    const totalMV = legs.reduce((s, p) => s + p.marketValue, 0);
    const totalCB = legs.reduce((s, p) => s + p.costBasis, 0);
    const totalTodayPL = legs.reduce((s, p) => s + p.todayPLDollar, 0);
    const totalPL = legs.reduce((s, p) => s + p.totalPLDollar, 0);
    const accountIds = new Set(legs.map((p) => p.accountId));
    const botIds = new Set(
      legs.map((p) => p.botId).filter((b): b is string => !!b)
    );
    const earliestOpened = legs
      .map((p) => p.openedAt)
      .filter((d) => typeof d === "string" && d.length >= 10)
      .sort()[0] ?? head.openedAt;
    out.push({
      id: `merged:${ticker}`,
      accountId: legs.length === 1 ? head.accountId : "",
      // botId left undefined when merged; drawer reads botCount instead.
      botId: undefined,
      asset: head.asset,
      assetName: head.assetName,
      assetClass: head.assetClass,
      side: head.side,
      qty: totalQty,
      avgEntryPrice: totalQty > 0 ? totalCB / totalQty : head.avgEntryPrice,
      currentPrice: head.currentPrice,
      marketValue: totalMV,
      costBasis: totalCB,
      dayChangePct: head.dayChangePct,
      todayPLDollar: totalTodayPL,
      todayPLPct: totalMV > 0 ? totalTodayPL / totalMV : 0,
      totalPLDollar: totalPL,
      totalPLPct: totalCB > 0 ? totalPL / totalCB : 0,
      sparkline: head.sparkline,
      openedAt: earliestOpened,
      accountCount: accountIds.size,
      botCount: botIds.size
    });
  }
  return out;
}
